import React, { useState, useEffect } from "react";
import Backdrop from "@mui/material/Backdrop";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import TableChartRoundedIcon from "@mui/icons-material/TableChartRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import StorageRoundedIcon from "@mui/icons-material/StorageRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";

import CustomDialog from "../Components/CustomDialog";
import CustomSnackbar from "../Components/CustomSnackbar";
import CustomTable from "../Components/CustomTable";
import PageLoader from "../Components/PageLoader";
import { getSettingsByModule } from "../utils/api";
import { EXTENSION_IDENTIFIER, ORG_VARS } from "../utils/orgVariables";
import { createModuleSync, updateModuleSync } from "../utils/moduleSync";
import {
  fetchExtensionSettings,
  fetchSingleOrgVariable,
  fetchFilteredModuleFields,
} from "../utils/zoho";
import { diffModuleAndDatabaseFields } from "../utils/fields";
import { SYNC_DURATIONS } from "../utils/constants";
import {
  BORDER,
  BORDER_DARK,
  BORDER_LIGHT,
  BRAND_COLOR,
  BRAND_COLOR_DARK,
  BRAND_COLOR_TEXT,
  BRAND_COLOR_TINT,
  CHIP_NEUTRAL_BG,
  SURFACE,
  SURFACE_TINT,
  TEXT_MUTED,
  TEXT_ON_DARK,
  TEXT_SECONDARY,
} from "../utils/colors";

const ZOHO = window.ZOHO;

const Index = () => {
  const [finalSaveLoading, setFinalSaveLoading] = useState(false);
  const [moduleApiName, setModuleApiName] = useState(null);
  const [moduleInfo, setModuleInfo] = useState(null);
  const [moduleFields, setModuleFields] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [currentModuleFields, setCurrentModuleFields] = useState(null);
  const [currentDatabaseFields, setCurrentDatabaseFields] = useState([]);
  const [syncDuration, setSyncDuration] = useState({
    label: "24 Hours",
    total_in_minutes: 1440,
  });
  const [deleteBackupData, setDeleteBackupData] = useState(true);
  const [pageMode, setPageMode] = useState("");
  const [selectedModule, setSelectedModule] = useState(null);
  const [moduleDetails, setModuleDetails] = useState("MySQL Sync Settings");
  const [showBackdrop, setShowBackDrop] = useState(false);
  const [settingId, setModuleSettingId] = useState(null);
  const [dbConnectionUrl, setDbConnectionUrl] = useState(null);

  const [zohoLoaded, setZohoLoaded] = useState(false);
  const [dataCenterUrl, setDataCenterUrl] = useState("");
  const [zohoApiKey, setZohoApiKey] = useState();
  const [orgId, setOrgId] = useState();
  const [zapiKey, setZapiKey] = useState();

  //snackbar state and functions
  const [openSnack, setOpenSnack] = useState(false);
  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState(""); // success or error

  const auth = { orgId, apiKey: zohoApiKey };

  /** Push a saved setting (fetched from the backend) into the dialog state. */
  const applySettingToState = (settingRows, zohoFields) => {
    const settingId = settingRows[0].setting_id;
    const settingSchema = JSON.parse(settingRows[0].setting_schema);

    const { availableModuleFields, markedDatabaseFields } =
      diffModuleAndDatabaseFields(zohoFields, settingSchema?.database_fields);

    setModuleSettingId(settingId);
    setSyncDuration(settingSchema?.syncDuration);
    setDeleteBackupData(settingSchema?.deleteBackupData);
    setCurrentDatabaseFields(markedDatabaseFields);
    setCurrentModuleFields(availableModuleFields);
    setModuleDetails(settingSchema?.moduleDetails);

    return settingId;
  };

  useEffect(() => {
    async function initZoho() {
      ZOHO.embeddedApp.on("PageLoad", async function (data) {
        setModuleApiName(data.module);

        const settings = await fetchExtensionSettings();
        setDataCenterUrl(settings.dataCenterUrl);
        setZohoApiKey(settings.apiKey);
        setOrgId(settings.orgId);
        setZapiKey(settings.zapiKey);

        setZohoLoaded(true);
      });

      ZOHO.embeddedApp.init();
    }
    initZoho();
  }, []);

  useEffect(() => {
    if (zohoLoaded) {
      try {
        (async () => {
          //check db connection is exist or not
          const connectionUrl = await fetchSingleOrgVariable(
            ORG_VARS.DB_CONNECTION_URL
          );
          if (!connectionUrl) {
            setDbConnectionUrl(false);
            setLoading(false);
            return;
          }

          const zohoFields = await fetchFilteredModuleFields(moduleApiName);

          const data = await getSettingsByModule(auth, moduleApiName);
          const getAllModules = await ZOHO.CRM.META.getModules();
          const respectiveModuleData = getAllModules.modules?.find(
            (module) => module.api_name === moduleApiName
          );

          setDbConnectionUrl(connectionUrl);
          setModuleInfo(respectiveModuleData);
          setModuleFields(zohoFields);

          if (!data) {
            setCurrentModuleFields(zohoFields);
            setLoading(false);
            return;
          }

          const settingId = applySettingToState(data, zohoFields);
          setLoading(false);

          if (!settingId) {
            alert("Setting id not found.");
          }
        })();
      } catch (err) {
        setLoading(false);
      }
    }
  }, [zohoLoaded]);

  const handleClickSnack = () => {
    setOpenSnack(true);
  };

  const handleCloseSnack = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSnack(false);
  };

  const showError = (errorMessage) => {
    setMessage(errorMessage);
    setSeverity("error");
    handleClickSnack();
  };

  const handleSave = async () => {
    setShowBackDrop(true);
    try {
      if (pageMode === "addNewMode") {
        const { error, settingId: newSettingId } = await createModuleSync({
          auth,
          connectionUrl: dbConnectionUrl,
          dataCenterUrl,
          zapiKey,
          moduleApiName: moduleInfo.api_name,
          settingSchema: {
            moduleInfo: {
              api_name: moduleInfo.api_name,
              module_name: moduleInfo.plural_label,
            },
            moduleDetails: moduleDetails,
            database_fields: currentDatabaseFields,
            syncDuration: syncDuration,
            deleteBackupData: deleteBackupData,
          },
          syncDurationMinutes: syncDuration.total_in_minutes,
        });

        if (error) {
          showError(error);
          return;
        }

        setModuleSettingId(newSettingId);
      } else {
        const { error } = await updateModuleSync({
          auth,
          connectionUrl: dbConnectionUrl,
          dataCenterUrl,
          zapiKey,
          moduleApiName: moduleInfo.api_name,
          settingId,
          settingSchema: {
            moduleInfo: {
              api_name: moduleInfo.api_name,
              module_name: moduleInfo.module_name,
            },
            moduleDetails: "MySQL Sync Settings",
            database_fields: currentDatabaseFields,
            syncDuration: syncDuration,
            deleteBackupData: deleteBackupData,
          },
          syncDurationMinutes: syncDuration.total_in_minutes,
        });

        if (error) {
          showError(error);
          return;
        }
      }

      setOpen(false);
      setPageMode("");
    } catch (err) {
      showError(err?.message || String(err));
    } finally {
      // always end the loading state, even if a step above threw
      setShowBackDrop(false);
    }
  };

  const handleEditSetting = async () => {
    setShowBackDrop(true);
    const data = await getSettingsByModule(auth, moduleApiName);

    applySettingToState(data, moduleFields);
    setPageMode("editMode");
    setShowBackDrop(false);
    setOpen(true);
  };

  const handleSubmit = async () => {
    setFinalSaveLoading(true);

    if (settingId !== "") {
      setMessage("Successfull");
      setSeverity("success");
      handleClickSnack();
      setFinalSaveLoading(false);

      await ZOHO.CRM.ACTION.setConfig({
        settingId: settingId,
        orgid: orgId,
        datacenterurl: dataCenterUrl,
        connname: EXTENSION_IDENTIFIER,
      });
    } else {
      setMessage("Please create setting first!!!");
      setSeverity("error");
      handleClickSnack();
      setFinalSaveLoading(false);
    }
  };

  if (loading) {
    return <PageLoader fullScreen message="Loading module settings..." />;
  }

  if (!dbConnectionUrl)
    return (
      <Box
        sx={{
          py: 10,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1,
          textAlign: "center",
        }}
      >
        <Box
          sx={{
            width: 60,
            height: 60,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: BRAND_COLOR_TINT,
            mb: 1,
          }}
        >
          <StorageRoundedIcon sx={{ fontSize: 30, color: BRAND_COLOR }} />
        </Box>
        <Typography sx={{ fontSize: 17, fontWeight: 600 }}>
          Database not connected
        </Typography>
        <Typography sx={{ fontSize: 14, color: TEXT_MUTED }}>
          Go to the extension settings and set up the MySQL connection first.
        </Typography>
      </Box>
    );

  if (!settingId && !open) {
    return (
      <Box
        sx={{
          py: 10,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1,
          textAlign: "center",
        }}
      >
        <Box
          sx={{
            width: 60,
            height: 60,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: BRAND_COLOR_TINT,
            mb: 1,
          }}
        >
          <TableChartRoundedIcon sx={{ fontSize: 30, color: BRAND_COLOR }} />
        </Box>
        <Typography sx={{ fontSize: 17, fontWeight: 600 }}>
          No settings found for {moduleInfo.module_name}
        </Typography>
        <Typography sx={{ fontSize: 14, color: TEXT_MUTED }}>
          Configure which fields to sync before using this action.
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddRoundedIcon />}
          sx={{
            mt: 1.5,
            bgcolor: BRAND_COLOR,
            textTransform: "none",
            borderRadius: "8px",
            px: 3,
            "&:hover": { bgcolor: BRAND_COLOR_DARK },
          }}
          onClick={() => {
            setPageMode("addNewMode");
            setOpen(true);
          }}
        >
          Add Settings
        </Button>
      </Box>
    );
  }

  return (
    <>
      <Backdrop
        sx={{ color: TEXT_ON_DARK, zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showBackdrop}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <CustomSnackbar
        open={openSnack}
        handleClose={handleCloseSnack}
        message={message}
        severity={severity}
      />

      <Grid container spacing={2} p={2}>
        <Grid item xs={3.5}>
          <Box
            sx={{
              borderRadius: "10px",
              border: `1px solid ${BORDER_LIGHT}`,
              bgcolor: SURFACE,
              p: 2.5,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: BRAND_COLOR_TINT,
                  flexShrink: 0,
                }}
              >
                <TableChartRoundedIcon
                  sx={{ fontSize: 21, color: BRAND_COLOR }}
                />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 16, fontWeight: 600 }} noWrap>
                  {moduleInfo?.plural_label}
                </Typography>
                {moduleDetails && (
                  <Typography
                    sx={{ fontSize: 12.5, color: TEXT_MUTED }}
                    noWrap
                  >
                    {moduleDetails}
                  </Typography>
                )}
              </Box>
            </Box>

            <Box sx={{ display: "flex", gap: 1, mt: 2, flexWrap: "wrap" }}>
              <Chip
                size="small"
                label={`${currentDatabaseFields?.length || 0} fields`}
                sx={{
                  fontSize: 12,
                  bgcolor: CHIP_NEUTRAL_BG,
                  color: TEXT_SECONDARY,
                }}
              />
              {syncDuration?.label && (
                <Chip
                  size="small"
                  icon={<ScheduleRoundedIcon sx={{ fontSize: 14 }} />}
                  label={`Every ${syncDuration.label}`}
                  sx={{
                    fontSize: 12,
                    bgcolor: BRAND_COLOR_TINT,
                    color: BRAND_COLOR_TEXT,
                    "& .MuiChip-icon": { color: BRAND_COLOR_TEXT },
                  }}
                />
              )}
            </Box>

            <Divider sx={{ my: 1.75 }} />

            <Button
              size="small"
              variant="outlined"
              startIcon={<EditRoundedIcon sx={{ fontSize: 16 }} />}
              sx={{
                textTransform: "none",
                borderRadius: "6px",
                color: TEXT_SECONDARY,
                borderColor: BORDER,
                "&:hover": {
                  borderColor: BORDER_DARK,
                  bgcolor: SURFACE_TINT,
                },
              }}
              onClick={handleEditSetting}
            >
              Edit
            </Button>
          </Box>
        </Grid>
        <Grid item xs={8.5}>
          <CustomTable
            headers={["Selected Fields"]}
            rows={currentDatabaseFields}
            id="field_label"
          />
        </Grid>
      </Grid>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mr: 2, mt: 1 }}>
        <Button
          onClick={handleSubmit}
          variant="contained"
          sx={{
            px: 3,
            width: 130,
            bgcolor: BRAND_COLOR,
            textTransform: "none",
            borderRadius: "8px",
            "&:hover": { bgcolor: BRAND_COLOR_DARK },
          }}
        >
          {finalSaveLoading ? (
            <CircularProgress size={21} color="inherit" />
          ) : (
            "Save"
          )}
        </Button>
      </Box>

      {pageMode === "editMode" ? (
        <CustomDialog
          selectedModule={selectedModule}
          mode={pageMode}
          customAction={true}
          open={open}
          handleClose={() => {
            setOpen(false);
          }}
          currentModuleFields={currentModuleFields}
          setCurrentModuleFields={setCurrentModuleFields}
          currentDatabaseFields={currentDatabaseFields}
          setCurrentDatabaseFields={setCurrentDatabaseFields}
          syncDuration={syncDuration}
          setSyncDuration={setSyncDuration}
          deleteBackupData={deleteBackupData}
          setDeleteBackupData={setDeleteBackupData}
          syncDurations={SYNC_DURATIONS}
          handleSave={handleSave}
          syncAllFields={() => {
            setCurrentDatabaseFields(moduleFields);
            setTimeout(() => {
              setCurrentModuleFields([]);
            }, 100);
          }}
          unSelectAllFields={() => {
            setCurrentModuleFields(moduleFields);
            setCurrentDatabaseFields([]);
          }}
          handleDeleteField={(value) => {
            const filteredCurrentDatabaseFields = currentDatabaseFields.filter(
              (field) => field.api_name !== value.api_name
            );

            setCurrentDatabaseFields([...filteredCurrentDatabaseFields]);
          }}
        />
      ) : (
        <CustomDialog
          open={open}
          mode={pageMode}
          customAction={true}
          selectedModule={selectedModule}
          currentModuleFields={currentModuleFields}
          handleClose={() => {
            setOpen(false);
          }}
          syncAllFields={() => {
            setCurrentDatabaseFields([...moduleFields]);
            setTimeout(() => {
              setCurrentModuleFields([]);
            }, 100);
          }}
          unSelectAllFields={() => {
            setCurrentModuleFields([...moduleFields]);
            setCurrentDatabaseFields([]);
          }}
          syncDurations={SYNC_DURATIONS}
          setCurrentModuleFields={setCurrentModuleFields}
          currentDatabaseFields={currentDatabaseFields}
          setCurrentDatabaseFields={setCurrentDatabaseFields}
          syncDuration={syncDuration}
          setSyncDuration={setSyncDuration}
          deleteBackupData={deleteBackupData}
          setDeleteBackupData={setDeleteBackupData}
          handleSave={handleSave}
        />
      )}
    </>
  );
};

export default Index;
